import type { Coupon } from '@mathflat/domain/PodOrder'
import type { MixpanelVersionT } from '@mathflat/shared/@modules/mixpanel/MixpanelClient'

import type { Academy } from '@/dto/Academy'
import dateUtils from '@/utils/DateUtils'

export type UserPropertiesSchema = {
  D코드: Academy['academyId']
  academyGroupId: Academy['groupId']
  학원명: Academy['name']
  요금제: Academy['subscribePlan']
  사용제품: Academy['authority']
  프리미엄체험가능여부: Academy['metadata']['freePremiumAvailable']
  가입상태: Academy['status']
  CRM학원분류값: Academy['customerType']
  이메일: Academy['email']
  등록반수: Academy['lessonClassCount']
  등록학생수: Academy['studentCount']
  무료체험시작일: Academy['freeStartDate']
  무료체험종료일: Academy['freeEndDate']
  정식가입시작일: Academy['payStartDate']
  정식가입종료일: Academy['payEndDate']
  프리미엄체험시작일: Academy['freePremiumStartDate']
  프리미엄체험종료일: Academy['freePremiumEndDate']
  추가과금학생수: Academy['studentLimitCount']
  월기본구독료: Academy['monthlyBaseFee']
  월납입일: Academy['payDate']
  출결문자이용여부: Academy['metadata']['attendanceSmsService']
  SMS사용여부: Academy['metadata']['smsService']
  로그인도메인: Academy['academyAdditionalData']['organizationDomainName']
  기관명: Academy['academyAdditionalData']['organizationName']
  이닛셋팅여부: Academy['initialized']
  총판코드: Academy['sellerCode']
  정식가입설명회신청여부: Academy['academyAdditionalData']['contractContactRequestedFlag']
  팝업타겟팅값: Academy['metadata']['popupTargetingGroup']
  로고설정타입: Academy['logoOperation']
  프린팅쿠폰목록: {
    쿠폰이름: string
    쿠폰ID: number
    쿠폰혜택: ValueOf<typeof couponBenefitMap>
    쿠폰혜택값: number
    쿠폰만료일: Date
    쿠폰활성상태: boolean | undefined
  }[]
  유형별외부경유: Academy['signUpFrom']
  userPropertiesVersion: MixpanelVersionT
}

export const couponBenefitMap: Record<Coupon['benefitType'], string> = {
  DELIVERY_DISCOUNT: '배송비 무료',
  PRODUCT_DISCOUNT: '상품 할인',
  PRODUCT_FREE: '상품 무료',
  PRODUCT_PERCENT_DISCOUNT: '할인율 할인',
}

export const convertAcademyToUserProperties = ({
  academy,
}: {
  academy: Academy
}): Partial<UserPropertiesSchema> => {
  return {
    D코드: academy.academyId,
    academyGroupId: academy.groupId,
    학원명: academy.name,
    요금제: academy.subscribePlan,
    사용제품: academy.authority,
    프리미엄체험가능여부: academy.metadata.freePremiumAvailable,
    가입상태: academy.status,
    CRM학원분류값: academy.customerType,
    이메일: academy.email,
    등록반수: academy.lessonClassCount,
    등록학생수: academy.studentCount,
    무료체험시작일: dateUtils.formatDateStrZonedISO(academy.freeStartDate),
    무료체험종료일: dateUtils.formatDateStrZonedISO(academy.freeEndDate),
    정식가입시작일: dateUtils.formatDateStrZonedISO(academy.payStartDate),
    정식가입종료일: dateUtils.formatDateStrZonedISO(academy.payEndDate),
    프리미엄체험시작일: dateUtils.formatDateStrZonedISO(academy.freePremiumStartDate),
    프리미엄체험종료일: dateUtils.formatDateStrZonedISO(academy.freePremiumEndDate),
    추가과금학생수: academy.studentLimitCount,
    월기본구독료: academy.monthlyBaseFee,
    월납입일: academy.payDate,
    출결문자이용여부: academy.metadata.attendanceSmsService,
    SMS사용여부: academy.metadata.smsService,
    로그인도메인: academy.academyAdditionalData.organizationDomainName,
    기관명: academy.academyAdditionalData.organizationName,
    이닛셋팅여부: academy.initialized,
    총판코드: academy.sellerCode,
    정식가입설명회신청여부: academy.academyAdditionalData.contractContactRequestedFlag,
    팝업타겟팅값: academy.metadata.popupTargetingGroup,
    로고설정타입: academy.logoOperation,
  }
}
